import { useEffect, useMemo, useRef, useState } from 'react';
import { HelpCircle, Timer, Check, Play, ArrowRight, SkipForward } from 'lucide-react';

// Who Am I? — asymmetric classroom guessing game.
//  - Student sees a dark-green rectangle (image hidden). Asks yes/no questions aloud.
//  - Teacher sees the picture + "Guessed" button. Press when student figures it out.
//  - After Guessed: student also sees the picture (reveal). Teacher presses "Continue"
//    to show the next image.
//  - Optional timer: when enabled, pressing Guessed auto-advances without a reveal
//    step; the game ends when the timer runs out.
//
// Controlled component. VirtualClassroom owns the synced game state and feeds it
// through `gameState` + `onGameStateChange`.

const BRAND_GREEN = '#2A4D25';
const BRAND_GREEN_DARK = '#1e3a1a';
const DEFAULT_TIMER_SEC = 120;

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export const createInitialWhoAmIState = () => ({
  phase: 'idle', // 'idle' | 'playing' | 'reveal' | 'final'
  order: [],
  index: 0,
  guessedCount: 0,
  skippedCount: 0,
  timerEnabled: false,
  timerSec: DEFAULT_TIMER_SEC,
  timerStartedAt: null,
});

const startGame = (state, images, config) => {
  if (!images.length) return state;
  const order = shuffle(images.map((i) => i.id));
  return {
    ...state,
    phase: 'playing',
    order,
    index: 0,
    guessedCount: 0,
    skippedCount: 0,
    timerEnabled: !!config.timerEnabled,
    timerSec: config.timerSec || DEFAULT_TIMER_SEC,
    timerStartedAt: config.timerEnabled ? Date.now() : null,
  };
};

const markGuessed = (state) => {
  const nextIndex = state.index + 1;
  const guessed = state.guessedCount + 1;
  const outOfImages = nextIndex >= state.order.length;
  if (state.timerEnabled) {
    // Timed mode: skip the reveal step; advance immediately. End if out of images.
    return {
      ...state,
      index: nextIndex,
      guessedCount: guessed,
      phase: outOfImages ? 'final' : 'playing',
    };
  }
  // Untimed mode: pause on reveal so both see the image.
  return { ...state, guessedCount: guessed, phase: 'reveal' };
};

const continueAfterReveal = (state) => {
  const nextIndex = state.index + 1;
  if (nextIndex >= state.order.length) {
    return { ...state, index: nextIndex, phase: 'final' };
  }
  return { ...state, index: nextIndex, phase: 'playing' };
};

const skipImage = (state) => {
  const nextIndex = state.index + 1;
  const skipped = state.skippedCount + 1;
  const outOfImages = nextIndex >= state.order.length;
  return {
    ...state,
    index: nextIndex,
    skippedCount: skipped,
    phase: outOfImages ? 'final' : 'playing',
  };
};

const endGame = (state) => ({ ...state, phase: 'final', timerStartedAt: null });

const resetGame = () => createInitialWhoAmIState();

// -------- Timer hook (counts down based on wall-clock start) --------

const useTimeLeft = (state) => {
  const [now, setNow] = useState(() => Date.now());
  const isCountingDown = state.phase === 'playing' && state.timerEnabled && state.timerStartedAt;
  useEffect(() => {
    if (!isCountingDown) return undefined;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [isCountingDown]);
  if (!state.timerEnabled || !state.timerStartedAt) return null;
  if (state.phase === 'final') return 0;
  return Math.max(0, state.timerSec - Math.floor((now - state.timerStartedAt) / 1000));
};

// -------- Static preview (editor canvas thumbnail) --------

const StaticPreview = () => (
  <div
    className="w-full h-full flex flex-col items-center justify-center rounded-2xl border-2 border-dashed p-4 text-white"
    style={{ background: BRAND_GREEN, borderColor: BRAND_GREEN_DARK }}
  >
    <div className="text-lg font-semibold">Who Am I?</div>
  </div>
);

// -------- UI atoms --------

const TimerBadge = ({ seconds }) => (
  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white text-brand-primary text-sm font-semibold shadow-sm">
    <Timer size={14} />
    <span>{String(Math.floor(seconds / 60)).padStart(2, '0')}:{String(seconds % 60).padStart(2, '0')}</span>
  </div>
);

const PrimaryButton = ({ children, onClick, disabled, icon: Icon, variant = 'solid' }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed ${
      variant === 'solid'
        ? 'text-white hover:brightness-110'
        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
    }`}
    style={variant === 'solid' ? { background: BRAND_GREEN } : undefined}
  >
    {Icon && <Icon size={16} />}
    <span>{children}</span>
  </button>
);

const HiddenRectangle = ({ timeLeft, hintText }) => (
  <div
    className="w-full h-full rounded-2xl flex flex-col items-center justify-center p-6 text-white relative overflow-hidden"
    style={{ background: BRAND_GREEN }}
  >
    <HelpCircle size={64} className="opacity-80 mb-3" />
    <div className="text-base font-semibold opacity-90 text-center">
      {hintText || 'Who am I?'}
    </div>
    <div className="text-xs opacity-70 mt-1 text-center max-w-xs">
      Став запитання, на які можна відповісти так або ні.
    </div>
    {timeLeft != null && (
      <div className="absolute top-3 right-3">
        <TimerBadge seconds={timeLeft} />
      </div>
    )}
  </div>
);

const ImageCard = ({ url, overlay, timeLeft, footer }) => (
  <div
    className="w-full h-full rounded-2xl flex flex-col items-center justify-between p-3 bg-white relative overflow-hidden"
    style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}
  >
    <div className="w-full flex items-center justify-between gap-2 text-xs text-gray-500">
      <span>{overlay || ''}</span>
      {timeLeft != null ? (
        <div className="text-brand-primary">
          <TimerBadge seconds={timeLeft} />
        </div>
      ) : (
        <span />
      )}
    </div>
    <div className="flex-1 w-full flex items-center justify-center overflow-hidden my-2">
      {url ? (
        <img
          src={url}
          alt=""
          className="max-w-full max-h-full object-contain rounded-lg"
          draggable={false}
        />
      ) : (
        <div className="text-gray-400 text-sm italic">Image not found</div>
      )}
    </div>
    {footer}
  </div>
);

const FinalScreen = ({ state, canReset, onReset }) => {
  const total = state.order.length;
  return (
    <div className="w-full h-full flex flex-col items-center justify-center p-6 gap-4 text-center">
      <div className="text-xl font-bold text-brand-primary">Гру завершено</div>
      <div className="grid grid-cols-2 gap-6">
        <div>
          <div className="text-4xl font-bold" style={{ color: BRAND_GREEN }}>{state.guessedCount}</div>
          <div className="text-xs text-gray-500 mt-0.5">Відгадано</div>
        </div>
        <div>
          <div className="text-4xl font-bold text-gray-700">{total}</div>
          <div className="text-xs text-gray-500 mt-0.5">Всього</div>
        </div>
      </div>
      {canReset && (
        <PrimaryButton onClick={onReset} icon={Play}>Зіграти ще</PrimaryButton>
      )}
    </div>
  );
};

// -------- Core game surface --------

const WhoAmIGameCore = ({ images, state, onChange, userRole, previewMode, config }) => {
  const timeLeft = useTimeLeft(state);
  const timerEndedRef = useRef(false);

  // Auto-end game when timer hits 0.
  useEffect(() => {
    const isCountingDown = state.phase === 'playing' && state.timerEnabled;
    if (!isCountingDown) {
      timerEndedRef.current = false;
      return;
    }
    if (timeLeft === 0 && !timerEndedRef.current) {
      timerEndedRef.current = true;
      onChange(endGame(state));
    }
  }, [timeLeft, state, onChange]);

  const isTeacher = previewMode || userRole === 'teacher';
  const canControl = isTeacher; // Teacher runs the game; students only read.

  const imageById = useMemo(() => {
    const map = new Map();
    images.forEach((img) => map.set(img.id, img));
    return map;
  }, [images]);

  const currentId = state.order[state.index];
  const currentImage = currentId ? imageById.get(currentId) : null;

  if (state.phase === 'idle') {
    const canStart = canControl && images.length > 0;
    return (
      <div className="w-full h-full flex flex-col items-center justify-center p-6 gap-3 text-center">
        <div className="text-lg font-semibold text-gray-800">Who Am I?</div>
        <div className="text-sm text-gray-500 max-w-sm">
          {isTeacher
            ? 'Учень ставитиме yes/no запитання. Відповідай усно і натисни «Відгадано», коли вгадає.'
            : 'Став yes/no запитання вчителю, щоб вгадати картинку.'}
        </div>
        {canStart ? (
          <PrimaryButton onClick={() => onChange(startGame(state, images, config))} icon={Play}>
            Старт
          </PrimaryButton>
        ) : isTeacher ? (
          <div className="text-xs text-amber-600 italic">Додай хоча б одну картинку в налаштуваннях.</div>
        ) : (
          <div className="text-xs text-gray-400 italic">Очікуємо вчителя…</div>
        )}
      </div>
    );
  }

  if (state.phase === 'final') {
    return (
      <FinalScreen
        state={state}
        canReset={canControl}
        onReset={() => onChange(resetGame())}
      />
    );
  }

  const positionLabel = `${state.index + 1} / ${state.order.length}`;

  if (state.phase === 'reveal') {
    // Both see the image. Teacher advances with "Continue".
    return (
      <ImageCard
        url={currentImage?.url}
        overlay={positionLabel}
        timeLeft={null}
        footer={
          <div className="w-full flex flex-wrap items-center justify-center gap-2">
            {canControl ? (
              <PrimaryButton onClick={() => onChange(continueAfterReveal(state))} icon={ArrowRight}>
                Продовжити
              </PrimaryButton>
            ) : (
              <div className="text-xs text-gray-500 italic">Очікуємо наступну картинку…</div>
            )}
          </div>
        }
      />
    );
  }

  // phase === 'playing'
  if (isTeacher) {
    return (
      <ImageCard
        url={currentImage?.url}
        overlay={positionLabel}
        timeLeft={timeLeft}
        footer={
          <div className="w-full flex flex-wrap items-center justify-center gap-2">
            <button
              onClick={() => onChange(skipImage(state))}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition"
            >
              <SkipForward size={14} /> Пропустити
            </button>
            <PrimaryButton onClick={() => onChange(markGuessed(state))} icon={Check}>
              Відгадано
            </PrimaryButton>
          </div>
        }
      />
    );
  }

  // Student playing view: hidden rectangle + (optional) timer.
  return <HiddenRectangle timeLeft={timeLeft} />;
};

// -------- Public entry point --------

const WhoAmIElement = ({
  element,
  isEditor = false,
  previewMode = false,
  userRole = null,
  gameState = null,
  onGameStateChange = null,
}) => {
  const content = element?.content || {};
  const images = useMemo(() => {
    const list = Array.isArray(content.images) ? content.images : [];
    return list
      .filter((img) => img && typeof img.url === 'string' && img.url.trim().length > 0)
      .map((img, idx) => ({ id: img.id || `img_${idx}`, url: img.url }));
  }, [content.images]);

  const config = {
    timerEnabled: !!content.timerEnabled,
    timerSec: Number.isFinite(content.timerSec) && content.timerSec > 0 ? content.timerSec : DEFAULT_TIMER_SEC,
  };

  if (isEditor) {
    return <StaticPreview />;
  }

  if (previewMode) {
    return <WhoAmIPreviewShell images={images} config={config} />;
  }

  const state = gameState || createInitialWhoAmIState();
  const handleChange = (next) => {
    if (onGameStateChange) onGameStateChange(next);
  };
  return (
    <WhoAmIGameCore
      images={images}
      state={state}
      onChange={handleChange}
      userRole={userRole}
      previewMode={false}
      config={config}
    />
  );
};

const WhoAmIPreviewShell = ({ images, config }) => {
  const [state, setState] = useState(() => createInitialWhoAmIState());
  return (
    <WhoAmIGameCore
      images={images}
      state={state}
      onChange={setState}
      userRole={null}
      previewMode
      config={config}
    />
  );
};

export default WhoAmIElement;
